import type { QueryHookOptions } from '@apollo/client';
import { useQuery } from '@apollo/client';

import type {
  GetUpgradePaths,
  GetUpgradePaths_userV2_AuthenticatedUser_upgradePaths,
} from '../components/ProductUpgradeMessage/__generated__/GetUpgradePaths';
import { GET_UPGRADE_PATHS_QUERY } from '../components/ProductUpgradeMessage/ProductUpgradeMessage.queries';

export interface UpgradePath {
  title: string;
  baseProduct: {
    title: string;
    sdbsId: number;
  };
}

export type UpgradeVariants = Record<number, UpgradePath>;

const getUpgradeVariants = (
  upgradePaths: GetUpgradePaths_userV2_AuthenticatedUser_upgradePaths[],
) =>
  upgradePaths?.reduce((acc: UpgradeVariants, item): UpgradeVariants => {
    if (!item) {
      return acc;
    }

    const { title: parentTitle, licensedproduct_id: parentSdbsId } = item;
    const newItems = item.upgrade_path.reduce((subAcc, subItem) => {
      if (!subItem) {
        return subAcc;
      }
      const { licensedproduct_id, title } = subItem;
      return {
        ...subAcc,
        [licensedproduct_id]: {
          title,
          baseProduct: {
            title: parentTitle,
            sdbsId: parentSdbsId,
          },
        },
      };
    }, {});
    return { ...acc, ...newItems };
  }, {});

const useUpgradePaths = (queryOptions?: QueryHookOptions) => {
  const { data, loading, error } = useQuery<GetUpgradePaths>(
    GET_UPGRADE_PATHS_QUERY,
    queryOptions,
  );

  const upgradePaths =
    data?.userV2?.__typename === 'AuthenticatedUser'
      ? data?.userV2?.upgradePaths
      : [];

  const customerEmail =
    data?.userV2?.__typename === 'AuthenticatedUser'
      ? data?.userV2?.email
      : undefined;

  const upgradeVariants = getUpgradeVariants(upgradePaths);

  return { loading, error, customerEmail, upgradePaths, upgradeVariants };
};

export default useUpgradePaths;
